import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/services/api";
import {
  Shield,
  Users,
  Settings2,
  ArrowRightLeft,
  CheckCircle2,
  AlertTriangle,
  Search,
  Loader2,
  RefreshCw,
} from "lucide-react";

const PERFIL_COLORS = {
  admin: "bg-purple-100 text-purple-800",
  embajador_digital: "bg-amber-100 text-amber-800",
  vendedor_carrito: "bg-orange-100 text-orange-800",
  distribuidor_mayorista: "bg-blue-100 text-blue-800",
  casa_matriz: "bg-teal-100 text-teal-800",
  domiciliario: "bg-green-100 text-green-800",
  supervisor: "bg-gray-200 text-gray-800",
};

export default function AdminCapacidades() {
  const [catalogo, setCatalogo] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("usuarios");

  // Config modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userCaps, setUserCaps] = useState(null);
  const [saving, setSaving] = useState(false);

  // Migración
  const [migrando, setMigrando] = useState(false);
  const [reporte, setReporte] = useState(null);
  const [reportesPrevios, setReportesPrevios] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [catRes, usersRes, repRes] = await Promise.all([
        api.get("/capacidades/catalogo"),
        api.get("/usuarios"),
        api.get("/capacidades/admin/migracion/reportes"),
      ]);
      setCatalogo(catRes.data);
      setUsuarios(usersRes.data);
      setReportesPrevios(repRes.data);
    } catch (e) {
      toast.error("Error cargando datos de capacidades");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openConfig = async (u) => {
    setSelectedUser(u);
    setUserCaps(null);
    try {
      const res = await api.get(`/capacidades/usuario/${u.id}`);
      setUserCaps(res.data);
    } catch (e) {
      toast.error("Error cargando capacidades del usuario");
      setSelectedUser(null);
    }
  };

  const toggleCap = (cap) => {
    if (!userCaps) return;
    const defaults = new Set(userCaps.capacidades_default);
    const extra = new Set(userCaps.capacidades_extra || []);
    const removidas = new Set(userCaps.capacidades_removidas || []);
    const efectiva = (defaults.has(cap) || extra.has(cap)) && !removidas.has(cap);

    if (efectiva) {
      // desactivar
      if (extra.has(cap)) extra.delete(cap);
      if (defaults.has(cap)) removidas.add(cap);
    } else {
      // activar
      removidas.delete(cap);
      if (!defaults.has(cap)) extra.add(cap);
    }
    setUserCaps({
      ...userCaps,
      capacidades_extra: [...extra],
      capacidades_removidas: [...removidas],
    });
  };

  const changePerfil = (perfil) => {
    if (!userCaps || !catalogo) return;
    setUserCaps({
      ...userCaps,
      perfil_comercial: perfil,
      capacidades_default: catalogo.defaults[perfil] || [],
      capacidades_extra: [],
      capacidades_removidas: [],
    });
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/capacidades/usuario/${selectedUser.id}`, {
        perfil_comercial: userCaps.perfil_comercial,
        capacidades_extra: userCaps.capacidades_extra,
        capacidades_removidas: userCaps.capacidades_removidas,
      });
      toast.success("Capacidades actualizadas");
      setSelectedUser(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const ejecutarMigracion = async () => {
    if (!window.confirm("¿Ejecutar la migración de Vendedores Digitales → Embajador Digital? Es segura e idempotente: conserva login, historial y links.")) return;
    setMigrando(true);
    try {
      const res = await api.post("/capacidades/admin/migracion/embajadores");
      setReporte(res.data);
      toast.success(`Migración completada: ${res.data.migrados} migrados`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la migración");
    } finally {
      setMigrando(false);
    }
  };

  const perfilLabel = (clave) =>
    catalogo?.perfiles.find((p) => p.clave === clave)?.label || clave || "—";

  const resolvePerfil = (u) => {
    if (u.perfil_comercial) return u.perfil_comercial;
    if (u.rol === "admin") return "admin";
    if (u.rol === "supervisor") return "supervisor";
    if (u.rol === "casa_matriz") return "casa_matriz";
    if (u.rol === "domiciliario") return "domiciliario";
    if (u.tipo_vendedor === "digital") return "embajador_digital";
    return "vendedor_carrito";
  };

  const gruposCapacidades = catalogo
    ? catalogo.capacidades.reduce((acc, c) => {
        (acc[c.grupo] = acc[c.grupo] || []).push(c);
        return acc;
      }, {})
    : {};

  const filteredUsers = usuarios.filter(
    (u) =>
      u.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A2C0A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-capacidades-page">
      <div>
        <h1 className="text-2xl font-bold text-[#4A2C0A] flex items-center gap-2">
          <Shield className="w-6 h-6" /> Perfiles y Capacidades
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Red Comercial Digital: control de acceso por capacidades y migración de perfiles
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={tab === "usuarios" ? "default" : "outline"}
          onClick={() => setTab("usuarios")}
          className={tab === "usuarios" ? "bg-[#4A2C0A]" : ""}
          data-testid="tab-usuarios"
        >
          <Users className="w-4 h-4 mr-2" /> Usuarios y Capacidades
        </Button>
        <Button
          variant={tab === "migracion" ? "default" : "outline"}
          onClick={() => setTab("migracion")}
          className={tab === "migracion" ? "bg-[#4A2C0A]" : ""}
          data-testid="tab-migracion"
        >
          <ArrowRightLeft className="w-4 h-4 mr-2" /> Migración
        </Button>
      </div>

      {tab === "usuarios" && (
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="relative mb-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-usuarios-input"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase border-b">
                  <th className="py-2 pr-4">Usuario</th>
                  <th className="py-2 pr-4">Perfil Comercial</th>
                  <th className="py-2 pr-4 hidden sm:table-cell">Ajustes</th>
                  <th className="py-2 pr-4 hidden sm:table-cell">Estado</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => {
                  const perfil = resolvePerfil(u);
                  return (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-amber-50/40">
                      <td className="py-3 pr-4">
                        <p className="font-medium text-[#4A2C0A]">{u.nombre}</p>
                        <p className="text-xs text-gray-500">{u.email}</p>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge className={`${PERFIL_COLORS[perfil] || "bg-gray-100"} border-0`}>
                          {perfilLabel(perfil)}
                        </Badge>
                        {!u.perfil_comercial && (
                          <p className="text-[10px] text-gray-400 mt-1">por rol (no explícito)</p>
                        )}
                      </td>
                      <td className="py-3 pr-4 hidden sm:table-cell text-xs text-gray-600">
                        {(u.capacidades_extra?.length || 0) > 0 && (
                          <span className="text-green-700">+{u.capacidades_extra.length} extra </span>
                        )}
                        {(u.capacidades_removidas?.length || 0) > 0 && (
                          <span className="text-red-600">−{u.capacidades_removidas.length} removidas</span>
                        )}
                        {!(u.capacidades_extra?.length || u.capacidades_removidas?.length) && "—"}
                      </td>
                      <td className="py-3 pr-4 hidden sm:table-cell">
                        <Badge variant={u.activo ? "default" : "secondary"} className={u.activo ? "bg-green-100 text-green-800 border-0" : ""}>
                          {u.activo ? "Activo" : "Inactivo"}
                        </Badge>
                      </td>
                      <td className="py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openConfig(u)}
                          data-testid={`config-caps-btn-${u.email}`}
                        >
                          <Settings2 className="w-4 h-4 mr-1" /> Configurar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "migracion" && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-[#4A2C0A] mb-2">
              Migración: Vendedores Digitales → Embajador Digital
            </h2>
            <ul className="text-sm text-gray-600 space-y-1 mb-4 list-disc pl-5">
              <li>Conservan su login actual (mismo email y contraseña en /login)</li>
              <li>Al iniciar sesión verán la experiencia de Embajador</li>
              <li>Se conserva historial de pedidos, comisiones, link de catálogo y datos bancarios</li>
              <li>Se genera código de afiliado y registro de embajador vinculado</li>
              <li>Es idempotente: puede ejecutarse varias veces sin duplicar</li>
            </ul>
            <Button
              onClick={ejecutarMigracion}
              disabled={migrando}
              className="bg-[#4A2C0A] hover:bg-[#3a2208]"
              data-testid="ejecutar-migracion-btn"
            >
              {migrando ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRightLeft className="w-4 h-4 mr-2" />
              )}
              Ejecutar Migración
            </Button>
          </div>

          {reporte && <ReporteMigracion reporte={reporte} destacado />}

          {reportesPrevios.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#4A2C0A]">Reportes de migraciones anteriores</h3>
                <Button size="sm" variant="ghost" onClick={loadData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                {reportesPrevios.map((r) => (
                  <ReporteMigracion key={r.id} reporte={r} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Config Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={(o) => !o && setSelectedUser(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#4A2C0A]">
              Capacidades: {selectedUser?.nombre}
            </DialogTitle>
            <DialogDescription>{selectedUser?.email}</DialogDescription>
          </DialogHeader>

          {!userCaps ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#4A2C0A]" />
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Perfil Comercial
                </label>
                <Select value={userCaps.perfil_comercial} onValueChange={changePerfil}>
                  <SelectTrigger data-testid="perfil-comercial-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogo?.perfiles.map((p) => (
                      <SelectItem key={p.clave} value={p.clave}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Cambiar el perfil restablece las capacidades a las predeterminadas
                </p>
              </div>

              {Object.entries(gruposCapacidades).map(([grupo, caps]) => (
                <div key={grupo}>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {grupo}
                  </h4>
                  <div className="space-y-2">
                    {caps.map((c) => {
                      const defaults = new Set(userCaps.capacidades_default);
                      const extra = new Set(userCaps.capacidades_extra || []);
                      const removidas = new Set(userCaps.capacidades_removidas || []);
                      const activa = (defaults.has(c.clave) || extra.has(c.clave)) && !removidas.has(c.clave);
                      const esDefault = defaults.has(c.clave);
                      return (
                        <div
                          key={c.clave}
                          className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-800">{c.label}</p>
                            <p className="text-[11px] text-gray-400 font-mono">
                              {c.clave}
                              {esDefault && " · predeterminada del perfil"}
                              {extra.has(c.clave) && " · extra"}
                              {removidas.has(c.clave) && " · removida"}
                            </p>
                          </div>
                          <Switch
                            checked={activa}
                            onCheckedChange={() => toggleCap(c.clave)}
                            data-testid={`cap-switch-${c.clave}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setSelectedUser(null)}>
                  Cancelar
                </Button>
                <Button
                  onClick={saveConfig}
                  disabled={saving}
                  className="bg-[#4A2C0A] hover:bg-[#3a2208]"
                  data-testid="save-caps-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Guardar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ReporteMigracion = ({ reporte, destacado }) => (
  <div
    className={`rounded-2xl border p-5 ${
      destacado ? "bg-green-50 border-green-200" : "bg-white"
    }`}
    data-testid="reporte-migracion"
  >
    <div className="flex items-center gap-2 mb-3">
      {reporte.errores?.length > 0 ? (
        <AlertTriangle className="w-5 h-5 text-amber-600" />
      ) : (
        <CheckCircle2 className="w-5 h-5 text-green-600" />
      )}
      <h3 className="font-semibold text-[#4A2C0A]">
        Reporte de Migración — {new Date(reporte.fecha).toLocaleString("es-CO")}
      </h3>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
      <Stat label="Candidatos" value={reporte.total_candidatos} />
      <Stat label="Migrados" value={reporte.migrados} color="text-green-700" />
      <Stat label="Ya migrados" value={reporte.ya_migrados} />
      <Stat label="Errores" value={reporte.errores?.length || 0} color={reporte.errores?.length ? "text-red-600" : ""} />
      <Stat label="Usuarios antes" value={reporte.usuarios_antes} />
      <Stat label="Usuarios después" value={reporte.usuarios_despues} />
      <Stat label="Embajadores antes" value={reporte.embajadores_antes} />
      <Stat label="Embajadores después" value={reporte.embajadores_despues} />
    </div>
    <p className="text-xs mb-3">
      Validación integridad usuarios:{" "}
      {reporte.validacion_usuarios_ok ? (
        <span className="text-green-700 font-medium">OK — ningún usuario perdido</span>
      ) : (
        <span className="text-red-600 font-medium">REVISAR — conteo no coincide</span>
      )}
    </p>
    {reporte.detalle?.length > 0 && (
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-1 pr-3">Email</th>
              <th className="py-1 pr-3">Código afiliado</th>
              <th className="py-1 pr-3">Link catálogo</th>
              <th className="py-1 pr-3">Pedidos catálogo</th>
              <th className="py-1">Pedidos terceros</th>
            </tr>
          </thead>
          <tbody>
            {reporte.detalle.map((d, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1 pr-3">{d.email}</td>
                <td className="py-1 pr-3 font-mono">{d.codigo_afiliado}</td>
                <td className="py-1 pr-3">
                  {d.link_catalogo_ok ? (
                    <span className="text-green-700">/catalogo/{d.link_catalogo} ✓</span>
                  ) : (
                    <span className="text-gray-400">sin link</span>
                  )}
                </td>
                <td className="py-1 pr-3">{d.pedidos_catalogo_historicos}</td>
                <td className="py-1">{d.pedidos_terceros_historicos}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {reporte.errores?.length > 0 && (
      <div className="mt-3 bg-red-50 rounded-lg p-3">
        <p className="text-xs font-semibold text-red-700 mb-1">Registros no migrados:</p>
        {reporte.errores.map((e, i) => (
          <p key={i} className="text-xs text-red-600">
            {e.email}: {e.error}
          </p>
        ))}
      </div>
    )}
  </div>
);

const Stat = ({ label, value, color }) => (
  <div className="bg-white/70 rounded-lg px-3 py-2 border">
    <p className="text-[10px] text-gray-500 uppercase">{label}</p>
    <p className={`font-bold ${color || "text-[#4A2C0A]"}`}>{value}</p>
  </div>
);
